import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Radio, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsBreadToggle = (props: CustomizationDetailsBreadToggleProps) => {
  const { breadCalories: calories, isActive, isDisabled, onChange } = props;

  const { formatMessage } = useIntl();
  const yesHint = calories ? formatMessage(messages.calories, { calories }) : undefined;

  return (
    <View style={styles.container}>
      <View style={styles.separator} />

      <View style={styles.innerContainer}>
        <View style={styles.descriptionContainer}>
          <BodyText sizeMatch={['22']}>{formatMessage(messages.description)}</BodyText>
        </View>
      </View>

      <Radio.Group gap={theme.spacing['2']} itemsPerRow={2}>
        <Radio.Item
          palette="spinach"
          value="no"
          isSelected={!isActive}
          isDisabled={isDisabled}
          label={formatMessage(messages.noBreadLabel)}
          onPress={onChange}
        />

        <Radio.Item
          palette="spinach"
          value="yes"
          isSelected={isActive}
          isDisabled={isDisabled}
          label={formatMessage(messages.breadLabel)}
          hint={yesHint}
          onPress={onChange}
        />
      </Radio.Group>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  description: {
    defaultMessage: 'Would you like free bread?',
    description: 'Customization | Dressing details | Add bread description',
  },
  breadLabel: {
    defaultMessage: 'Add bread',
    description: 'Customization | Dressing details | Add bread button label',
  },
  noBreadLabel: {
    defaultMessage: 'No bread',
    description: 'Customization | Dressing details | No bread button label',
  },
  calories: {
    defaultMessage: '{calories} cals',
    description: 'Customization | Dressing details | Calories label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['6'],
  },
  descriptionContainer: {
    flex: 1,
  },
  innerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
  },
  separator: {
    marginTop: theme.spacing['2'],
    marginHorizontal: -theme.spacing['10'],
    height: 8,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderTopWidth: 1,
    borderTopColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsBreadToggleProps = {
  isActive: boolean;
  isDisabled: boolean;
  onChange: () => void;
  breadCalories: number | undefined;
};
